import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  AfterViewChecked,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Subscription, filter, finalize, map } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Account } from 'app/core/models';
import { AccountService } from 'app/core';
import { TaskListTemplate } from 'app/shared/modules/legacy-tasks/tasks.types';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { SettingsTaskTemplatesCreateTemplateDialog } from '../details/create-task-template-dialog/create-task-list-template-dialog.component';

@Component({
  selector: 'task-list-templates-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListTemplatesListComponent extends OnDestroyMixin implements OnInit, AfterViewChecked {
  public taskListTemplatesCount: number;
  public taskListTemplatesTableColumns: string[] = ['name', 'applicable_type', 'created_at', 'updated_at'];
  public dataSource: MatTableDataSource<TaskListTemplate>;

  private sortApplied: boolean = false;

  @ViewChild(MatSort) private sort: MatSort;

  private get account(): Account {
    return this.accountService.getCurrentAccount();
  }

  constructor(
    private readonly matDialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly accountService: AccountService,
    private readonly tasksService: LegacyTasksTabService,
    private readonly snackBar: MatSnackBar,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchTaskListTemplates();
  }

  ngAfterViewChecked(): void {
    this.applySortToDataSource();
  }

  /**
   * Get task templates, create a dataSource based on response and get task templates total count.
   */
  public fetchTaskListTemplates(): Subscription {
    this.tasksService.accountId = this.account.id;
    return this.tasksService
      .getTaskListTemplates()
      .pipe(
        untilComponentDestroyed(this),
        map((taskListTemplates) => {
          this.dataSource = new MatTableDataSource<TaskListTemplate>(taskListTemplates);
          this.taskListTemplatesCount = taskListTemplates.length;
        }),
        finalize(() => {
          this.cdRef.detectChanges();
        }),
      )
      .subscribe(() => this.applySortToDataSource());
  }

  public createNewTemplate(): void {
    this.matDialog
      .open(SettingsTaskTemplatesCreateTemplateDialog, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          createNewTemplate: true,
          saveAsTemplate: false,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result) => !!result),
      )
      .subscribe({
        next: (taskListTemplate: TaskListTemplate) => {
          if (taskListTemplate.id)
            this.snackBar.open('Template successfully created.', '', {
              duration: 3000,
            });
        },
        complete: () => {
          // Force matSort new data assignment.
          this.sortApplied = false;
          this.fetchTaskListTemplates();
        },
      });
  }

  // Check if the MatSort has been applied and apply it if not
  public applySortToDataSource(): void {
    if (this.dataSource && this.sort && !this.sortApplied) {
      this.dataSource.sort = this.sort;
      this.sortApplied = true;
    }
  }

  public trackByFn(index: number, item: TaskListTemplate): number {
    return item.id || index;
  }

  public getRelatedToType(rawValue: string): string {
    switch (rawValue) {
      case 'Project':
        return 'Deal';
      case 'PropertyTenant':
        return 'Lease';
      case 'PropertyUnit':
        return 'Unit';
      case 'Account':
        return 'Global';
      default:
        return rawValue;
    }
  }
}
